<template>
  <v-card>
    <v-card-title :class="headerColorClass">
      <span class="headline">{{ $t('ticketBooks') }}</span>
      <v-spacer/>
      <Icon
      @icon-clicked="$emit('close')"
      :tooltipText="$t('close')"
      iconColor="white"
      icon="mdi-close"
      :small="false"
      large/>
    </v-card-title>
    <v-card-text>
      <FormWrapper
      :buttonText="$t('confirm')"
      :buttonColor="headerColorClass"
      @submit="selectBooks">
        <v-row v-if="fixedAccount" class="mt-3">
          <v-col cols="10" class="d-flex align-center">
            <Icon
            icon="mdi-account-circle"
            :small="false"
            iconColor="grey"
            :tooltipText="$t('destinationAccount')"/>
            <span class="text--secondary mr-1">{{ $t('destinationAccount') }}:</span>
            <span>{{ destinationAccountName }}</span>
          </v-col>
        </v-row>
        <v-row v-else class="mt-3">
          <v-col cols="10">
            <v-autocomplete
            :label="$t('ticketBookAccount')"
            :items="ticketBookAccounts"
            multiple
            item-text="name"
            item-value="accountId"
            v-model="selectedAccountIds">
              <template #prepend-inner>
                <Icon
                icon="mdi-account-circle"
                :small="false"
                iconColor="grey"
                :tooltipText="$t('ticketBookAccount')"/>
              </template>
              <template #selection="{ item, index }">
                <v-chip
                  small
                  close
                  @click:close="removeAccount(index)"
                  class="ma-1">
                  {{ item.name }}
                </v-chip>
              </template>
              <template #append>
                <Icon
                :tooltipText="$t('eligibleTicketBookAccounts')"
                icon="mdi-information-outline"
                :small="false"
                iconColor="info"
                />
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row class="mb-1">
          <v-col cols="10">
            <LoadableComponent :loading="ticketBooksLoading">
              <v-autocomplete
              :label="$t('ticketBooks')"
              v-model="selectedTicketBooks"
              item-value="ticketBookId"
              :items="availableTicketBooks"
              :disabled="selectedAccountIds == null || selectedAccountIds.length === 0"
              multiple
              clearable>
                <template #selection="{ item, index }">
                  <v-chip
                    small
                    close
                    @click:close="removeTicketBook(index)"
                    class="ma-1">
                    {{ item.name }}
                  </v-chip>
                </template>
                <template #item="{ item }">
                  <v-container>
                    <v-row dense class="subtitle-1 align-center" style="font-weight: bold">
                      {{ item.name }}
                      <Icon
                      v-if="item.availablePageCount === 0"
                      class="ml-1"
                      icon="mdi-alert"
                      iconColor="warning"
                      :tooltipText="$t('noPagesAvailable')"/>
                    </v-row>
                    <v-row dense class="text-caption">
                      {{ $t('pages') }}: {{ pageRangeString(item.firstPage, item.lastPage) }}
                    </v-row>
                    <v-row dense class="text-caption" v-if="selectedAccountIds.length > 1">
                      {{ ticketBookAccountMap.get(item.accountId)?.name || item.accountId }}
                    </v-row>
                  </v-container>
                </template>
                <template #prepend-inner>
                  <Icon
                  icon="mdi-book-open-variant"
                  :small="false"
                  iconColor="grey"
                  :tooltipText="$t('ticketBooks')"/>
                </template>
              </v-autocomplete>
            </LoadableComponent>
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <Icon
            icon="mdi-plus"
            :small="false"
            :iconColor="canCreateTicketBooks ? 'primary' : 'grey'"
            :overrideClickable="!canCreateTicketBooks"
            :tooltipText="canCreateTicketBooks ? $t('createTicketBooks') : $t('userDoesNotHavePermissionToCreateTicketBooks')"
            @icon-clicked="openCreateTicketBooksDialog"/>
          </v-col>
        </v-row>
      </FormWrapper>
    </v-card-text>
    <v-dialog v-model="createTicketBooksDialog" max-width="700px">
      <TicketBookForm
      v-if="createTicketBooksDialog"
      :ticketBooks="createAccountTicketBooks"
      :headerColorClass="headerColorClass"
      :disabled="creatingTicketBooks"
      @close="createTicketBooksDialog = false"
      @create-ticket-books="createTicketBooks">
        <v-row v-if="createAccountId != null">
          <v-col cols="12">
            <span class="subtitle-1">{{ $t('existingTicketBooks') }}</span>
            <TicketBookTable
            class="mt-n5"
            :loading="ticketBooksLoading"
            :ticketBooks="createAccountTicketBooks"
            :headers="existingTicketBookHeaders"
            :itemsPerPageOptions="TicketBookConstants.ItemsPerPageOptions"
            readonly/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
            :label="$t('ticketBookAccount')"
            :items="ticketBookAccounts"
            item-text="name"
            item-value="accountId"
            v-model="createAccountId"
            :disabled="fixedAccount"
            :rules="[rules.required]">
              <template #prepend-inner>
                <Icon
                icon="mdi-account-circle"
                :small="false"
                iconColor="grey"
                :tooltipText="$t('ticketBookAccount')"/>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </TicketBookForm>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { TicketBookStatus, UserClaims } from '@/utils/Enumerations'
import { pageRangeString } from '@/utils/NumericMutations'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { ticketBookHeaders } from '@/headers/Account'
import fieldRules from '@/utils/rules.js'
import { TicketBookConstants } from '@/utils/constants.js'

const EXISTING_TICKET_BOOK_COLUMNS = ['status', 'name', 'tract', 'mappedPages']

export default {
  name: 'TicketBookSelector',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    TicketBookForm: () => import('@/components/account/ticketBooks/TicketBookForm.vue'),
    TicketBookTable: () => import('@/components/account/ticketBooks/TicketBookTable.vue')
  },

  props: {
    accountIds: { type: Array, required: true },
    disabled: { type: Boolean, default: false },
    propTicketBookIds: { type: Array, required: true },
    propTicketBooksAccountIds: { type: Array, default: undefined },
    tractId: { type: Number, default: undefined },
    contractId: { type: Number, default: undefined },
    fixedAccount: { type: Boolean, default: false },
    headerColorClass: { type: String, default: 'primary white--text' }
  },

  data: () => ({
    TicketBookConstants,
    rules: fieldRules.rules,
    ticketBooksByAccount: new Map(),
    ticketBooksLoading: false,
    selectedAccountIds: [],
    selectedTicketBooks: [],
    createTicketBooksDialog: false,
    createAccountId: undefined,
    creatingTicketBooks: false
  }),

  async created () {
    if (this.propTicketBooksAccountIds?.length === 0) return

    this.selectedAccountIds = [...this.propTicketBooksAccountIds ?? []]
    this.selectedTicketBooks = [...this.propTicketBookIds ?? []]
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ticketBookAccounts () {
      return this.allAccounts.filter(a => this.accountIds.includes(a.accountId))
    },

    destinationAccountName () {
      return this.ticketBookAccounts[0]?.name
    },

    ticketBookAccountMap () {
      const map = new Map()
      this.ticketBookAccounts.forEach(account => {
        map.set(account.accountId, account)
      })
      return map
    },

    selectedAccounts () {
      return this.allAccounts.filter(a => this.selectedAccountIds.includes(a.accountId))
    },

    availableTicketBooks () {
      if (this.selectedAccountIds == null || this.selectedAccountIds.length === 0) {
        return []
      }

      const ticketBooksForAccount = this.selectedAccountIds.flatMap(accountId => this.ticketBooksByAccount.get(accountId) || [])
      return ticketBooksForAccount.filter(tb => tb.status === TicketBookStatus.Available.value ||
        (tb.tractId != null && tb.tractId === this.tractId) ||
        (tb.contractId != null && tb.contractId === this.contractId))
    },

    canCreateTicketBooks () {
      return userAssignedClaim(UserClaims.AccountManager)
    },

    createAccountTicketBooks () {
      return this.ticketBooksByAccount.get(this.createAccountId) ?? []
    },

    existingTicketBookHeaders () {
      return ticketBookHeaders().filter(h => EXISTING_TICKET_BOOK_COLUMNS.includes(h.value))
    }
  },

  watch: {
    async createAccountId (accountId) {
      if (accountId == null || this.ticketBooksByAccount.has(accountId)) return
      await this.fetchTicketBooksForAccount(accountId)
    },

    selectedAccountIds: {
      async handler (accountIds, oldAccountIds) {
        if (oldAccountIds != null && !oldAccountIds.every(id => accountIds?.includes(id))) {
          this.selectedTicketBooks = []
        }
        if (accountIds == null) return
        await this.updateTicketBookMap()
      }
    }
  },

  methods: {
    pageRangeString,
    ...mapActions('account', ['fetchTicketBooks', 'createTicketBook']),

    removeTicketBook (index) {
      const updatedSelection = [...this.selectedTicketBooks]
      updatedSelection.splice(index, 1)
      this.selectedTicketBooks = updatedSelection
    },

    removeAccount (index) {
      const updatedSelection = [...this.selectedAccountIds]
      updatedSelection.splice(index, 1)
      this.selectedAccountIds = updatedSelection
    },

    async updateTicketBookMap () {
      const accountsToFetch = this.selectedAccountIds.filter(accountId => !this.ticketBooksByAccount.has(accountId))
      if (accountsToFetch.length === 0) return

      await Promise.all(
        accountsToFetch.map(accountId => this.fetchTicketBooksForAccount(accountId))
      )
    },

    selectBooks () {
      const selectedTicketBooks = this.availableTicketBooks.filter(tb => this.selectedTicketBooks.includes(tb.ticketBookId))

      const booksOverlap = this.doTicketBooksOverlap(selectedTicketBooks)
      if (booksOverlap === true) {
        this.setSnackError(this.$t('ticketBookPageOverlap'))
        return
      }

      this.$emit('ticket-books-selected', { selectedTicketBookIds: this.selectedTicketBooks, accountIds: Array.from(new Set(selectedTicketBooks.map(tb => tb.accountId))) })
    },

    doTicketBooksOverlap (ticketBooks) {
      if (ticketBooks.length < 2) return false

      const sorted = [...ticketBooks].sort((a, b) => a.firstPage - b.firstPage)

      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].firstPage <= sorted[i - 1].lastPage) {
          return true
        }
      }

      return false
    },

    async fetchTicketBooksForAccount (accountId) {
      this.ticketBooksLoading = true
      const ticketBooks = await this.fetchTicketBooks({ accountId, includeInUse: true })
      this.ticketBooksByAccount.set(accountId, ticketBooks)
      this.ticketBooksByAccount = new Map(this.ticketBooksByAccount) // Trigger reactivity
      setTimeout(_ => { this.ticketBooksLoading = false }, 300)
    },

    openCreateTicketBooksDialog () {
      if (!this.canCreateTicketBooks) return

      if (this.fixedAccount) {
        this.createAccountId = this.accountIds[0]
      } else {
        this.createAccountId = this.selectedAccountIds.length === 1 ? this.selectedAccountIds[0] : undefined
      }
      this.createTicketBooksDialog = true
    },

    async createTicketBooks (ticketBooks) {
      if (this.creatingTicketBooks) return

      this.creatingTicketBooks = true
      const accountId = this.createAccountId
      const createdTicketBookIds = []
      try {
        for (const ticketBook of ticketBooks) {
          const createdTicketBook = await this.createTicketBook({ accountId, ticketBook })
          createdTicketBookIds.push(createdTicketBook.ticketBookId)
        }
      } finally {
        this.ticketBooksByAccount.delete(accountId)
        await this.fetchTicketBooksForAccount(accountId)
        this.creatingTicketBooks = false
      }

      if (!this.selectedAccountIds.includes(accountId)) {
        this.selectedAccountIds = [...this.selectedAccountIds, accountId]
      }
      this.selectedTicketBooks = [...this.selectedTicketBooks, ...createdTicketBookIds]
      this.createTicketBooksDialog = false
      this.setSnack(this.$tc('ticketBooksCreated', createdTicketBookIds.length))
    }
  }
}
</script>
